import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { DashboardLayout } from '../../../layouts/DashboardLayout';
import { getWaAccounts, createWaAccount, updateWaAccount, deleteWaAccount } from '../../../services/api';
import { toast } from 'sonner';
import {
  MessageCircle, Plus, Edit2, Trash2, RefreshCw, Inbox, Users, Settings,
  CheckCircle, XCircle, Eye, EyeOff, X, Bot, FileText, Zap, Bell
} from 'lucide-react';

const NAV_ITEMS = [
  { label: 'Cuentas', path: '/superadmin/whatsapp', icon: MessageCircle },
  { label: 'Inbox', path: '/superadmin/whatsapp/inbox', icon: Inbox },
  { label: 'Auto-respuestas', path: '/superadmin/whatsapp/auto-replies', icon: Bot },
  { label: 'Plantillas', path: '/superadmin/whatsapp/templates', icon: FileText },
  { label: 'Resp. Rapidas', path: '/superadmin/whatsapp/quick-replies', icon: Zap },
  { label: 'Contactos', path: '/superadmin/whatsapp/contacts', icon: Users },
  { label: 'Eventos', path: '/superadmin/whatsapp/event-settings', icon: Settings },
  { label: 'Notificaciones', path: '/superadmin/whatsapp/notifications', icon: Bell },
];

const EMPTY_FORM = {
  display_name: '', phone_number: '', phone_number_id: '',
  waba_id: '', access_token: '', verify_token: '', business_id: '',
};

const WhatsAppAccounts = () => {
  const navigate = useNavigate();
  const [accounts, setAccounts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY_FORM);
  const [saving, setSaving] = useState(false);
  const [showTokens, setShowTokens] = useState({});

  const loadAccounts = useCallback(async () => {
    setLoading(true);
    try {
      const res = await getWaAccounts();
      setAccounts(res.data?.accounts || []);
    } catch (err) { toast.error('Error cargando cuentas'); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { loadAccounts(); }, [loadAccounts]);

  const openCreate = () => { setEditing(null); setForm(EMPTY_FORM); setShowModal(true); };
  const openEdit = (acc) => {
    setEditing(acc);
    setForm({
      display_name: acc.display_name || '',
      phone_number: acc.phone_number || '',
      phone_number_id: acc.phone_number_id || '',
      waba_id: acc.waba_id || '',
      access_token: '',
      verify_token: acc.verify_token || '',
      business_id: acc.business_id || '',
    });
    setShowModal(true);
  };

  const handleSave = async () => {
    if (!form.display_name || (!editing && !form.phone_number_id)) {
      toast.error('Completa los campos requeridos');
      return;
    }
    setSaving(true);
    try {
      if (editing) {
        const updates = {};
        if (form.display_name) updates.display_name = form.display_name;
        if (form.phone_number) updates.phone_number = form.phone_number;
        if (form.access_token) updates.access_token = form.access_token;
        if (form.verify_token) updates.verify_token = form.verify_token;
        if (form.business_id) updates.business_id = form.business_id;
        await updateWaAccount(editing.id, updates);
        toast.success('Cuenta actualizada');
      } else {
        await createWaAccount(form);
        toast.success('Cuenta creada');
      }
      setShowModal(false);
      loadAccounts();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Error guardando cuenta');
    } finally { setSaving(false); }
  };

  const handleDelete = async (acc) => {
    if (!window.confirm(`Desactivar cuenta "${acc.display_name}"?`)) return;
    try {
      await deleteWaAccount(acc.id);
      toast.success('Cuenta desactivada');
      loadAccounts();
    } catch { toast.error('Error desactivando'); }
  };

  const toggleToken = (id) => setShowTokens(p => ({ ...p, [id]: !p[id] }));
  const maskToken = (t) => t ? t.substring(0, 8) + '...' + t.substring(t.length - 4) : '';

  const active = accounts.filter(a => a.status === 'active');
  const inactive = accounts.filter(a => a.status !== 'active');

  return (
    <DashboardLayout>
      <div className="max-w-7xl mx-auto space-y-6">
        {/* Sub-nav */}
        <div className="flex items-center gap-1 overflow-x-auto pb-1 border-b border-slate-200">
          {NAV_ITEMS.map(item => {
            const Icon = item.icon;
            const isActive = item.path === '/superadmin/whatsapp';
            return (
              <button key={item.path} onClick={() => navigate(item.path)}
                className={`flex items-center gap-1.5 px-3 py-2 text-sm rounded-lg whitespace-nowrap transition-colors ${
                  isActive ? 'bg-green-100 text-green-700 font-medium' : 'text-slate-500 hover:bg-slate-100 hover:text-slate-700'
                }`}>
                <Icon className="w-4 h-4" /> {item.label}
              </button>
            );
          })}
        </div>

        {/* Header */}
        <div className="flex items-center justify-between flex-wrap gap-3">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-green-100 rounded-xl flex items-center justify-center">
              <MessageCircle className="w-5 h-5 text-green-700" />
            </div>
            <div>
              <h1 className="text-xl font-bold text-slate-900">WhatsApp Hub</h1>
              <p className="text-sm text-slate-500">Gestion de cuentas y numeros de WhatsApp Business</p>
            </div>
          </div>
          <div className="flex gap-2">
            <button onClick={openCreate}
              className="flex items-center gap-1.5 px-3 py-2 bg-green-600 text-white text-sm rounded-lg hover:bg-green-700">
              <Plus className="w-4 h-4" /> Nueva Cuenta
            </button>
            <button onClick={loadAccounts}
              className="flex items-center gap-1.5 px-3 py-2 bg-slate-100 text-slate-700 text-sm rounded-lg hover:bg-slate-200">
              <RefreshCw className="w-4 h-4" />
            </button>
          </div>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-3 gap-4">
          <div className="bg-white rounded-xl border border-slate-200 p-4 text-center">
            <p className="text-2xl font-bold text-slate-900">{accounts.length}</p>
            <p className="text-xs text-slate-500 mt-1">Total</p>
          </div>
          <div className="bg-white rounded-xl border border-slate-200 p-4 text-center">
            <p className="text-2xl font-bold text-green-600">{active.length}</p>
            <p className="text-xs text-slate-500 mt-1">Activas</p>
          </div>
          <div className="bg-white rounded-xl border border-slate-200 p-4 text-center">
            <p className="text-2xl font-bold text-slate-400">{inactive.length}</p>
            <p className="text-xs text-slate-500 mt-1">Inactivas</p>
          </div>
        </div>

        {/* Table */}
        <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
          {loading ? (
            <div className="p-12 text-center text-slate-400">Cargando...</div>
          ) : accounts.length === 0 ? (
            <div className="p-12 text-center text-slate-400">
              <MessageCircle className="w-12 h-12 mx-auto mb-3 text-slate-300" />
              <p className="font-medium">No hay cuentas registradas</p>
              <p className="text-sm mt-1">Agrega tu primer numero de WhatsApp Business</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-slate-50 text-left">
                  <tr>
                    <th className="px-4 py-3 font-medium text-slate-600">Nombre</th>
                    <th className="px-4 py-3 font-medium text-slate-600">Telefono</th>
                    <th className="px-4 py-3 font-medium text-slate-600">Phone Number ID</th>
                    <th className="px-4 py-3 font-medium text-slate-600">Access Token</th>
                    <th className="px-4 py-3 font-medium text-slate-600">Estado</th>
                    <th className="px-4 py-3 font-medium text-slate-600">Acciones</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {accounts.map((acc) => (
                    <tr key={acc.id} className="hover:bg-slate-50">
                      <td className="px-4 py-3 font-medium text-slate-900">{acc.display_name}</td>
                      <td className="px-4 py-3 text-slate-600">{acc.phone_number}</td>
                      <td className="px-4 py-3 text-slate-500 font-mono text-xs">{acc.phone_number_id}</td>
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-1">
                          <span className="text-xs font-mono text-slate-400">
                            {showTokens[acc.id] ? acc.access_token : maskToken(acc.access_token)}
                          </span>
                          <button onClick={() => toggleToken(acc.id)} className="text-slate-400 hover:text-slate-600">
                            {showTokens[acc.id] ? <EyeOff className="w-3.5 h-3.5" /> : <Eye className="w-3.5 h-3.5" />}
                          </button>
                        </div>
                      </td>
                      <td className="px-4 py-3">
                        {acc.status === 'active' ? (
                          <span className="inline-flex items-center gap-1 text-xs font-medium text-green-700 bg-green-50 px-2 py-1 rounded-full">
                            <CheckCircle className="w-3 h-3" /> Activa
                          </span>
                        ) : (
                          <span className="inline-flex items-center gap-1 text-xs font-medium text-slate-500 bg-slate-100 px-2 py-1 rounded-full">
                            <XCircle className="w-3 h-3" /> Inactiva
                          </span>
                        )}
                      </td>
                      <td className="px-4 py-3">
                        <div className="flex gap-1">
                          <button onClick={() => openEdit(acc)}
                            className="p-1.5 rounded-lg hover:bg-slate-100 text-slate-500 hover:text-blue-600">
                            <Edit2 className="w-4 h-4" />
                          </button>
                          {acc.status === 'active' && (
                            <button onClick={() => handleDelete(acc)}
                              className="p-1.5 rounded-lg hover:bg-red-50 text-slate-500 hover:text-red-600">
                              <Trash2 className="w-4 h-4" />
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>

      {/* Modal */}
      {showModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40">
          <div className="bg-white rounded-2xl shadow-xl w-full max-w-lg mx-4 max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between p-5 border-b border-slate-200">
              <h2 className="text-lg font-bold text-slate-900">
                {editing ? 'Editar Cuenta' : 'Nueva Cuenta WhatsApp'}
              </h2>
              <button onClick={() => setShowModal(false)} className="text-slate-400 hover:text-slate-600">
                <X className="w-5 h-5" />
              </button>
            </div>
            <div className="p-5 space-y-4">
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Nombre *</label>
                <input value={form.display_name} onChange={e => setForm(p => ({ ...p, display_name: e.target.value }))}
                  className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-green-500 focus:border-green-500"
                  placeholder="Mi Negocio WhatsApp" />
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Telefono</label>
                <input value={form.phone_number} onChange={e => setForm(p => ({ ...p, phone_number: e.target.value }))}
                  className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-green-500 focus:border-green-500"
                  placeholder="+521234567890" />
              </div>
              {!editing && (
                <>
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-1">Phone Number ID *</label>
                    <input value={form.phone_number_id} onChange={e => setForm(p => ({ ...p, phone_number_id: e.target.value }))}
                      className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm font-mono focus:ring-2 focus:ring-green-500 focus:border-green-500"
                      placeholder="123456789012345" />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-1">WABA ID *</label>
                    <input value={form.waba_id} onChange={e => setForm(p => ({ ...p, waba_id: e.target.value }))}
                      className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm font-mono focus:ring-2 focus:ring-green-500 focus:border-green-500"
                      placeholder="123456789012345" />
                  </div>
                </>
              )}
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">
                  Access Token {editing ? '(dejar vacio para no cambiar)' : '*'}
                </label>
                <input value={form.access_token} onChange={e => setForm(p => ({ ...p, access_token: e.target.value }))}
                  className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm font-mono focus:ring-2 focus:ring-green-500 focus:border-green-500"
                  placeholder="EAAx..." type="password" />
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Verify Token (webhook)</label>
                <input value={form.verify_token} onChange={e => setForm(p => ({ ...p, verify_token: e.target.value }))}
                  className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm font-mono focus:ring-2 focus:ring-green-500 focus:border-green-500"
                  placeholder="mi_token_secreto" />
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Business ID (opcional)</label>
                <input value={form.business_id} onChange={e => setForm(p => ({ ...p, business_id: e.target.value }))}
                  className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-green-500 focus:border-green-500"
                  placeholder="UUID del negocio" />
              </div>
            </div>
            <div className="flex justify-end gap-2 p-5 border-t border-slate-200">
              <button onClick={() => setShowModal(false)}
                className="px-4 py-2 text-sm text-slate-700 bg-slate-100 rounded-lg hover:bg-slate-200">
                Cancelar
              </button>
              <button onClick={handleSave} disabled={saving}
                className="px-4 py-2 text-sm text-white bg-green-600 rounded-lg hover:bg-green-700 disabled:opacity-50">
                {saving ? 'Guardando...' : editing ? 'Actualizar' : 'Crear'}
              </button>
            </div>
          </div>
        </div>
      )}
    </DashboardLayout>
  );
};

export default WhatsAppAccounts;
